import { useEffect } from "react";
import { useLanguage } from "@/hooks/use-language";

const EN_NOTE = {
  da: "Dette juridiske dokument fås på engelsk.",
  de: "Dieses Rechtsdokument ist auf Englisch verfügbar.",
  es: "Este documento legal está disponible en inglés.",
  fr: "Ce document juridique est fourni en anglais.",
  it: "Questo documento legale è fornito in inglese.",
  nl: "Dit juridische document is in het Engels beschikbaar.",
  pl: "Ten dokument prawny jest dostępny w języku angielskim.",
  pt: "Este documento legal é fornecido em inglês.",
  sv: "Detta juridiska dokument tillhandahålls på engelska.",
  zh: "本法律文件以英文提供。",
  ja: "この法的文書は英語で提供されています。",
  ko: "이 법률 문서는 영어로 제공됩니다.",
};

const Section = ({ title, children }) => (
  <div className="border-t border-border py-8">
    <h2 className="font-display font-bold text-lg" data-testid={`legal-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{title}</h2>
    <div className="mt-3 text-sm leading-relaxed text-muted-foreground space-y-3">{children}</div>
  </div>
);

const LegalLayout = ({ tag, title, children }) => {
  const { lang } = useLanguage();
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="pt-40 pb-24" data-testid={`legal-${tag.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="max-w-3xl mx-auto px-6">
        <p className="font-display text-xs tracking-[0.35em] uppercase text-teal-600 dark:text-teal-400">{tag}</p>
        <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tight">{title}</h1>
        <p className="mt-4 text-xs uppercase tracking-[0.25em] text-muted-foreground">Last updated: September 2026</p>
        {EN_NOTE[lang] && (
          <p className="mt-4 inline-block border border-border px-4 py-2 text-xs text-muted-foreground" data-testid="legal-lang-note">
            {EN_NOTE[lang]}
          </p>
        )}
        <div className="mt-12">{children}</div>
      </div>
    </main>
  );
};

export const Privacy = () => (
  <LegalLayout tag="Legal" title="Privacy Policy">
    <Section title="Data Controller">
      <p>
        Streamline360 ApS, Vestergade 29, DK-1456 Copenhagen K, Denmark (VAT nr. DK46390261) is the
        data controller for personal data collected through this website. Contact us at
        privacy@streamline360.ai for any privacy-related request.
      </p>
    </Section>
    <Section title="What We Collect">
      <p>
        <strong className="text-foreground">Demo requests:</strong> when you submit the demo form we store
        your name, company, work email and message.
      </p>
      <p>
        <strong className="text-foreground">Visitor logs:</strong> when you browse this site we store your
        IP address, browser and device type (user agent), referring page, pages visited and a timestamp.
      </p>
    </Section>
    <Section title="Purpose and Legal Basis (GDPR)">
      <p>
        Demo request data is processed to respond to your enquiry and prepare a technical demonstration
        (Art. 6(1)(b) GDPR — steps prior to a contract).
      </p>
      <p>
        Visitor logs are processed to secure and improve the website and to understand aggregate traffic
        (Art. 6(1)(f) GDPR — legitimate interest). Logs are never used to build individual profiles.
      </p>
    </Section>
    <Section title="Storage and Retention">
      <p>
        Data is stored on servers within the EU. Visitor logs are retained for a maximum of 12 months;
        demo request data is kept for up to 24 months after our last correspondence, unless a contract
        requires longer retention.
      </p>
    </Section>
    <Section title="Sharing">
      <p>
        We do not sell personal data. Data is only processed by our hosting and infrastructure providers
        under data processing agreements, and is never transferred outside the EU/EEA without adequate
        safeguards under Chapter V GDPR.
      </p>
    </Section>
    <Section title="Your Rights">
      <p>
        You have the right to access, rectification, erasure, restriction of processing, data portability
        and to object to processing (Art. 15–21 GDPR). To exercise any right, email
        privacy@streamline360.ai — we respond within 30 days.
      </p>
      <p>
        You may lodge a complaint with the Danish Data Protection Agency: Datatilsynet, Carl Jacobsens
        Vej 35, 2500 Valby, Denmark (dt@datatilsynet.dk).
      </p>
    </Section>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout tag="Legal" title="Terms of Use">
    <Section title="The Website">
      <p>
        This website is operated by Streamline360 ApS, Vestergade 29, DK-1456 Copenhagen K, Denmark
        (VAT nr. DK46390261). Content is provided for general information about our private industrial
        AI platform and does not constitute a binding offer.
      </p>
    </Section>
    <Section title="Intellectual Property">
      <p>
        All content, trademarks, logos and design on this site are the property of Streamline360 ApS and
        may not be reproduced without prior written consent.
      </p>
    </Section>
    <Section title="No Warranty">
      <p>
        Information on this site, including benchmark figures, is provided "as is" and may be updated or
        changed without notice. Benchmark results refer to the specific test setup described and may vary
        in other environments.
      </p>
    </Section>
    <Section title="Liability">
      <p>
        We are liable in accordance with Danish law. We are not liable for indirect losses or for the
        content of external sites linked from this website.
      </p>
    </Section>
    <Section title="Governing Law">
      <p>
        These terms are governed by Danish law. Any dispute is subject to the exclusive jurisdiction of
        the courts of Copenhagen, Denmark.
      </p>
    </Section>
  </LegalLayout>
);
